import React from 'react';
import { ceoHubService, CeoAnnouncementItem } from '../services/ceoHubService';

const priorityBg: Record<string, string> = {
  URGENT:  'bg-red-600',
  WARNING: 'bg-amber-500',
  INFO:    'bg-blue-600',
};

const priorityText: Record<string, string> = {
  URGENT:  'text-white',
  WARNING: 'text-white',
  INFO:    'text-white',
};

const priorityBadge: Record<string, string> = {
  URGENT:  'bg-red-800/60 text-red-100',
  WARNING: 'bg-amber-700/60 text-amber-100',
  INFO:    'bg-blue-800/60 text-blue-100',
};

interface AnnouncementBannerProps {
  currentUserId: string;
}

export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ currentUserId }) => {
  const [items,    setItems]    = React.useState<CeoAnnouncementItem[]>([]);
  const [index,    setIndex]    = React.useState(0);
  const [acking,   setAcking]   = React.useState(false);
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());

  // Poll every 60 s for new announcements
  const load = React.useCallback(async () => {
    try {
      const res = await ceoHubService.getAnnouncements();
      // Only show unacknowledged
      setItems(res.filter(a => !a.myAck));
    } catch { /* silent */ }
  }, []);

  React.useEffect(() => {
    void load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Reset index when items change
  React.useEffect(() => { setIndex(0); }, [items.length]);

  const visible = items.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  const current = visible[Math.min(index, visible.length - 1)];
  if (!current) return null;

  const handleAck = async () => {
    setAcking(true);
    try {
      await ceoHubService.acknowledgeAnnouncement(current.id);
      setDismissed(prev => new Set([...prev, current.id]));
      setItems(prev => prev.filter(a => a.id !== current.id));
    } finally { setAcking(false); }
  };

  const goNext = () => setIndex(i => (i + 1) % visible.length);
  const goPrev = () => setIndex(i => (i - 1 + visible.length) % visible.length);

  const bg    = priorityBg[current.priority]    ?? 'bg-slate-700';
  const text  = priorityText[current.priority]  ?? 'text-white';
  const badge = priorityBadge[current.priority] ?? 'bg-slate-600 text-white';

  return (
    <div className={`relative flex w-full items-center gap-3 px-4 py-2.5 shadow-md ${bg} z-50`}>
      {/* priority badge */}
      <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${badge}`}>
        {current.priority}
      </span>

      {/* scrolling title + body */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex items-baseline gap-2">
          <span className={`flex-shrink-0 text-[13px] font-black ${text}`}>{current.title}</span>
          <span className={`truncate text-[12px] opacity-80 ${text}`}>{current.body}</span>
        </div>
      </div>

      {/* nav dots (multiple announcements) */}
      {visible.length > 1 && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={goPrev}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:text-white transition">
            <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
              <path d="M10 3L6 8l4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="text-[11px] font-semibold text-white/80">{index + 1}/{visible.length}</span>
          <button type="button" onClick={goNext}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:text-white transition">
            <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
              <path d="M6 3l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* acknowledge button */}
      <button
        type="button"
        onClick={handleAck}
        disabled={acking}
        className="flex-shrink-0 rounded-full bg-white/20 hover:bg-white/30 px-3.5 py-1.5 text-[11px] font-bold text-white transition disabled:opacity-50 border border-white/30"
      >
        {acking ? '…' : '✓ Got it'}
      </button>
    </div>
  );
};
